import React from 'react';
import { Box, Typography, Chip, Button, Tooltip } from '@material-ui/core';
import { useTranslation } from 'react-i18next';
import { makeStyles } from '@material-ui/core/styles';
import { getDisplayableFilters } from 'src/components/Search/utils/getDisplayableFilters';
import { getSummaryKey, summaryRenderValue } from '../utils';
import { IFiltersSummary } from './types';

const useStyles = makeStyles((theme) => ({
    truncateText: {
        whiteSpace: 'nowrap',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        maxWidth: '300px',
    },
    container: {
        padding: `0px ${theme.spacing(2)}px`,
    },
}));

/**
 * Represents the filters summary component that displays the current filters applied.
 *
 * @param {{ filters: IFiltersSummary<TField> }} { onClear, filters, noDisplayableFilters }
 * @return {*}
 */
export const FiltersSummary = <TField,>({
    onClear,
    filters,
    noDisplayableFilters,
}: IFiltersSummary<TField>) => {
    const { t } = useTranslation();
    const classes = useStyles();

    const labelSummary: string = t('ModalFilters.summary');
    const labelClear: string = t('ModalFilters.clear');

    const displayableFilters = getDisplayableFilters(
        filters,
        noDisplayableFilters
    );

    if (displayableFilters.length === 0) return null;

    return (
        <Box
            style={{
                display: 'flex',
                alignItems: 'center',
                alignSelf: 'center',
                gap: 20,
                flexWrap: 'wrap',
            }}
            className={classes.container}
        >
            <Typography variant="subtitle2" data-testid="filters-summary-title">
                {labelSummary}
            </Typography>
            <Box
                style={{
                    display: 'flex',
                    gap: 10,
                    flexWrap: 'wrap',
                    alignItems: 'center',
                }}
            >
                {displayableFilters.map(([key, value]) => {
                    const rendered = summaryRenderValue(value, key, t);

                    const clearKey = getSummaryKey(key);

                    const label: string = `${t(
                        `ModalFilters.${clearKey}`
                    )}: ${rendered}`;

                    return (
                        <Tooltip key={key} title={label}>
                            <Chip
                                size="medium"
                                label={label}
                                color="primary"
                                variant="outlined"
                                data-testid={`filters-summary-chip-${key}`}
                                style={{ maxWidth: '300px' }}
                                classes={{ label: classes.truncateText }}
                            />
                        </Tooltip>
                    );
                })}
                <Button
                    onClick={onClear}
                    style={{
                        textTransform: 'capitalize',
                        minWidth: 100,
                    }}
                    variant="contained"
                    color="primary"
                    data-testid="filters-summary-clear-button"
                    size="small"
                >
                    {labelClear}
                </Button>
            </Box>
        </Box>
    );
};
